/**
 * POM-6534 — the three groups of the Review Lot & NDC grid, derived from each row's status.
 *
 * Grouping is presentation only: the classifier still decides every status, and nothing here
 * changes which rows print, block or can be edited.
 */

import { BATCH_ROW_STATUS } from './batchLotStatus';
import { ROW_STATUS } from './bulkLotNdcStatus';
import { statusCountsSummary } from './bulkLotNdcSummaries';

export const REVIEW_GROUP = {
  PENDING_COMMIT: 'PENDING_COMMIT',
  NEEDS_ATTENTION: 'NEEDS_ATTENTION',
  NO_ACTION_NEEDED: 'NO_ACTION_NEEDED',
};

export const REVIEW_GROUP_TITLE = {
  [REVIEW_GROUP.PENDING_COMMIT]: 'Pending commit',
  [REVIEW_GROUP.NEEDS_ATTENTION]: 'Needs attention',
  [REVIEW_GROUP.NO_ACTION_NEEDED]: 'No action needed',
};

// Workflow order, not A–Z: what waits on a commit, then what needs a decision, then what is done.
const REVIEW_GROUP_ORDER = [
  REVIEW_GROUP.PENDING_COMMIT,
  REVIEW_GROUP.NEEDS_ATTENTION,
  REVIEW_GROUP.NO_ACTION_NEEDED,
];

const NO_ACTION_NEEDED_STATUSES = new Set([
  ROW_STATUS.AS_ALLOCATED,
  ROW_STATUS.NO_ALTERNATIVE,
  ROW_STATUS.SHORTFALL_COMMITTED,
  ROW_STATUS.MANUAL_LOT_COMMITTED,
]);

export const reviewGroupFor = (status) => {
  if (status === ROW_STATUS.CHANGED || status === BATCH_ROW_STATUS.CLAIM_PENDING) {
    return REVIEW_GROUP.PENDING_COMMIT;
  }
  if (NO_ACTION_NEEDED_STATUSES.has(status)) return REVIEW_GROUP.NO_ACTION_NEEDED;

  // Every other status, and any added later, is surfaced rather than tucked away.
  return REVIEW_GROUP.NEEDS_ATTENTION;
};

export const compareReviewGroupOrder = (leftGroup, rightGroup) =>
  REVIEW_GROUP_ORDER.indexOf(leftGroup) - REVIEW_GROUP_ORDER.indexOf(rightGroup);

export const isDispensedOrCountLocked = (status) =>
  status === ROW_STATUS.ALREADY_DISPENSED || status === ROW_STATUS.LOCKED_FOR_COUNT;

/**
 * Per group: how many rows it holds, how many survive the filters, and the per-status counts its
 * header states. Counted over the whole group, not the filtered rows, so the header describes the
 * group while "x of y" says how much of it is on screen.
 */
export const summarizeReviewGroups = ({ reviewRows, visibleRows, isFiltered, unverifiedQty }) => {
  const shownIds = new Set(visibleRows.map((row) => row.item.requestLineItemId));

  const groups = Object.fromEntries(
    REVIEW_GROUP_ORDER.map((group) => {
      const inGroup = reviewRows.filter((row) => reviewGroupFor(row.status) === group);

      return [
        group,
        {
          total: inGroup.length,
          shown: inGroup.filter((row) => shownIds.has(row.item.requestLineItemId)).length,
          statusCounts: statusCountsSummary(inGroup),
          claimCount: inGroup.filter((row) => row.status === BATCH_ROW_STATUS.CLAIM_PENDING).length,
        },
      ];
    })
  );

  return { isFiltered, unverifiedQty: unverifiedQty ?? 0, groups };
};

const describePlaceholderText = ({ group, total }) => {
  if (total > 0) return 'No rows here match the filters.';
  if (group === REVIEW_GROUP.NEEDS_ATTENTION) {
    return 'Nothing needs attention. Every row in this batch can print as it is.';
  }

  return 'No rows here.';
};

/**
 * One full-width line for a group with nothing on screen. AG Grid draws no group row for a group
 * without children, so without this an empty group would vanish instead of saying it is empty.
 * Pending commit is the exception: it exists only while something is staged.
 */
export const buildGroupPlaceholderRows = (summary) =>
  REVIEW_GROUP_ORDER.flatMap((group) => {
    const { total, shown } = summary.groups[group];
    if (shown > 0) return [];
    if (group === REVIEW_GROUP.PENDING_COMMIT && total === 0) return [];

    return [
      {
        id: `group-placeholder-${group}`,
        reviewGroup: group,
        isGroupPlaceholder: true,
        placeholderText: describePlaceholderText({ group, total }),
      },
    ];
  });
